import React, { useEffect, useState } from "react";
import api from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import DatePicker from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { inr, fmtDate, downloadCSV, monthLabel } from "@/lib/tps";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, LineChart, Line, CartesianGrid } from "recharts";
import { Download } from "lucide-react";
import { format } from "date-fns";

const COLORS = { revenue: "#B86F77", expenses: "#141E30", profit: "#10B981" };

export default function Reports() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    const p = new URLSearchParams();
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    const { data } = await api.get(`/reports/summary?${p}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  // helpers for filename
  const fileLabel = () => {
    if (start && end) return `${start}_to_${end}`;
    if (start) return `from_${start}`;
    const now = new Date();
    return format(now, "MMMM_yyyy");
  };

  const exportMonthly = () => {
    if (!data) return;
    const rows = data.monthly.map((m) => ({
      Month: monthLabel(m.month).split(" ")[0],
      Year: m.month.split("-")[0],
      Revenue: m.revenue,
      Expenses: m.expenses,
      "Profit/Loss": m.profit,
    }));
    downloadCSV(`The_Palate_Stories_Monthly_Report_${fileLabel()}.csv`, rows.length ? rows : [{ Month: "", Year: "", Revenue: 0, Expenses: 0, "Profit/Loss": 0 }]);
  };

  const exportMonthlyPL = async () => {
    if (!data) return;
    // For each month, compute orders count, advance, balance from orders list
    const p = new URLSearchParams();
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    const { data: orders } = await api.get(`/orders?${p}`);
    const grp = {};
    orders.forEach((o) => {
      const m = (o.required_date || "").slice(0, 7);
      if (!m) return;
      const g = grp[m] = grp[m] || { orders: [], delivered: 0, cancelled: 0, advance: 0, balance: 0, total: 0 };
      g.orders.push(o);
      if (o.status === "Delivered") g.delivered++;
      if (o.status === "Cancelled" || o.status === "Lost") g.cancelled++;
      const isValid = o.status !== "Cancelled" && o.status !== "Lost";
      if (isValid) {
        g.total += o.total_price || 0;
        g.advance += o.advance_amount || 0;
        if (o.status !== "Delivered") g.balance += o.balance_amount || 0;
      }
    });
    const rows = data.monthly.map((m) => {
      const g = grp[m.month] || { orders: [], delivered: 0, cancelled: 0, advance: 0, balance: 0, total: 0 };
      const total = g.orders.length;
      const valid = g.orders.filter((o) => o.status !== "Cancelled" && o.status !== "Lost").length;
      return {
        Month: monthLabel(m.month).split(" ")[0],
        Year: m.month.split("-")[0],
        "Total Orders": total,
        Revenue: m.revenue,
        Expenses: m.expenses,
        "Profit/Loss": m.profit,
        "Advance Received": g.advance,
        "Balance Pending": g.balance,
        "Delivered Orders": g.delivered,
        "Cancelled Orders": g.cancelled,
        "Average Order Value": valid ? (m.revenue / valid).toFixed(2) : 0,
      };
    });
    if (rows.length === 0) {
      rows.push({ Month: "", Year: "", "Total Orders": 0, Revenue: 0, Expenses: 0, "Profit/Loss": 0, "Advance Received": 0, "Balance Pending": 0, "Delivered Orders": 0, "Cancelled Orders": 0, "Average Order Value": 0 });
    }
    downloadCSV(`The_Palate_Stories_Monthly_PL_${fileLabel()}.csv`, rows);
  };

  const exportOrders = async () => {
    const p = new URLSearchParams();
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    const { data: orders } = await api.get(`/orders?${p}`);
    const rows = orders.map((o) => ({
      "Order ID": o.order_id,
      "Customer Name": o.customer_name,
      "Phone": o.customer_phone,
      "Required Date": o.required_date,
      "Required Time": o.required_time,
      "Theme": o.items?.[0]?.theme || "",
      "Weight": o.items?.[0]?.weight || "",
      "Flavour": o.items?.[0]?.flavour || "",
      "Delivery Type": o.delivery_type || "",
      "Delivery Partner": o.delivery_method || "",
      "Total Price": o.total_price || 0,
      "Advance Paid": o.advance_amount || 0,
      "Balance Amount": o.balance_amount || 0,
      "Payment Method": o.payment_method || "",
      "Payment Status": o.payment_status || "",
      "Status": o.status,
    }));
    downloadCSV(`The_Palate_Stories_Orders_${fileLabel()}.csv`, rows.length ? rows : [{ "Order ID": "" }]);
  };

  const exportExpenses = async () => {
    const p = new URLSearchParams();
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    const { data: exps } = await api.get(`/expenses?${p}`);
    const rows = exps.map((e) => ({
      "Expense Date": e.expense_date,
      "Category": e.category,
      "Description": e.description || "",
      "Amount": e.amount || 0,
      "Payment Method": e.payment_method || "",
      "Notes": e.notes || "",
    }));
    downloadCSV(`The_Palate_Stories_Expenses_${fileLabel()}.csv`, rows.length ? rows : [{ "Expense Date": "" }]);
  };

  if (!data) return <div className="p-6 text-sm text-[#4B5563]">Loading…</div>;

  return (
    <div className="space-y-3 animate-fade-in" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-heading text-2xl md:text-3xl">Reports</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <DatePicker value={start} onChange={setStart} placeholder="Start" testId="rep-start" />
          <DatePicker value={end} onChange={setEnd} placeholder="End" testId="rep-end" />
          {(start || end) && <Button variant="ghost" className="h-8" onClick={() => { setStart(""); setEnd(""); }} data-testid="rep-clear">Clear</Button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={exportMonthly} variant="outline" className="h-8 border-[#E8E3DF]" data-testid="export-monthly-btn"><Download className="h-3.5 w-3.5 mr-1" /> Export Monthly</Button>
        <Button onClick={exportMonthlyPL} variant="outline" className="h-8 border-[#E8E3DF]" data-testid="export-monthly-pl-btn"><Download className="h-3.5 w-3.5 mr-1" /> Export Monthly P&L</Button>
        <Button onClick={exportOrders} variant="outline" className="h-8 border-[#E8E3DF]" data-testid="export-orders-btn"><Download className="h-3.5 w-3.5 mr-1" /> Export Orders</Button>
        <Button onClick={exportExpenses} variant="outline" className="h-8 border-[#E8E3DF]" data-testid="export-expenses-btn"><Download className="h-3.5 w-3.5 mr-1" /> Export Expenses</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Revenue" value={inr(data.revenue)} c="#B86F77" testId="kpi-revenue" />
        <Kpi label="Expenses" value={inr(data.expenses)} c="#141E30" testId="kpi-expenses" />
        <Kpi label="Profit / Loss" value={inr(data.profit)} c={data.profit >= 0 ? "#10B981" : "#EF4444"} testId="kpi-profit" />
        <Kpi label="Total Orders" value={data.total_orders} testId="kpi-total" />
        <Kpi label="Avg Order Value" value={inr(data.avg_order_value)} testId="kpi-avg" />
        <Kpi label="Advance Received" value={inr(data.advance_received)} testId="kpi-advance" />
        <Kpi label="Pending Balance" value={inr(data.pending_balance)} testId="kpi-pending" />
        <Kpi label="Delivered / Cancelled" value={`${data.delivered} / ${data.cancelled}`} testId="kpi-delivered" />
      </div>

      <Tabs defaultValue="monthly">
        <TabsList className="bg-[#FCF9F9]">
          <TabsTrigger value="monthly" data-testid="tab-monthly">Month-wise</TabsTrigger>
          <TabsTrigger value="daily" data-testid="tab-daily">Date-wise</TabsTrigger>
        </TabsList>

        <TabsContent value="monthly" className="space-y-3">
          <Card className="border-[#E8E3DF]"><CardContent className="p-4">
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={data.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DF" />
                  <XAxis dataKey="month" stroke="#141E30" fontSize={11} />
                  <YAxis stroke="#141E30" fontSize={11} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E8E3DF" }} formatter={(v) => inr(v)} />
                  <Legend />
                  <Bar dataKey="revenue" fill={COLORS.revenue} />
                  <Bar dataKey="expenses" fill={COLORS.expenses} />
                  <Bar dataKey="profit" fill={COLORS.profit} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent></Card>
          <ReportTable rows={data.monthly} dateLabel="Month" dateKey="month" />
        </TabsContent>

        <TabsContent value="daily" className="space-y-3">
          <Card className="border-[#E8E3DF]"><CardContent className="p-4">
            <div className="h-64">
              <ResponsiveContainer>
                <LineChart data={data.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DF" />
                  <XAxis dataKey="date" stroke="#141E30" fontSize={10} />
                  <YAxis stroke="#141E30" fontSize={11} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E8E3DF" }} formatter={(v) => inr(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke={COLORS.revenue} strokeWidth={2} />
                  <Line type="monotone" dataKey="expenses" stroke={COLORS.expenses} strokeWidth={2} />
                  <Line type="monotone" dataKey="profit" stroke={COLORS.profit} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent></Card>
          <ReportTable rows={data.daily} dateLabel="Date" dateKey="date" fmt={fmtDate} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ label, value, c, testId }) {
  return (
    <Card className="border-[#E8E3DF]" data-testid={testId}>
      <CardContent className="p-3">
        <div className="text-[11px] uppercase tracking-wider text-[#4B5563]">{label}</div>
        <div className="font-heading text-xl mt-1" style={{ color: c || "#141E30" }}>{value}</div>
      </CardContent>
    </Card>
  );
}

function ReportTable({ rows, dateLabel, dateKey, fmt }) {
  return (
    <Card className="border-[#E8E3DF] overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="tps-table">
          <TableHeader className="bg-[#FCF9F9]">
            <TableRow>
              <TableHead className="py-2">{dateLabel}</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
              <TableHead className="text-right">Expenses</TableHead>
              <TableHead className="text-right">Profit / Loss</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={4} className="py-6 text-center text-[#4B5563]">No data.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r[dateKey]}>
                <TableCell className="py-2 font-medium">{fmt ? fmt(r[dateKey]) : r[dateKey]}</TableCell>
                <TableCell className="py-2 text-right">{inr(r.revenue)}</TableCell>
                <TableCell className="py-2 text-right">{inr(r.expenses)}</TableCell>
                <TableCell className="py-2 text-right" style={{ color: r.profit >= 0 ? "#065F46" : "#991B1B" }}>{inr(r.profit)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
